import {
  ArrowDownLeft,
  ArrowLeftRight,
  CreditCard,
  Gift,
  Landmark,
  ShoppingCart,
  Split,
  Target,
  Undo2,
  User,
  type LucideIcon,
} from 'lucide-react'
import { SHARED_EXPENSES_ENABLED } from '@/lib/featureFlags'

export type AddKind =
  | 'spent'
  | 'split'
  | 'paidForOthers'
  | 'received'
  | 'refund'
  | 'cardBill'
  | 'transfer'
  | 'emi'
  | 'goal'
  | 'lend'

export type KindTone = 'out' | 'in' | 'move'

export interface KindInfo {
  kind: AddKind
  label: string
  hint: string
  /** The form's heading; defaults to the label. */
  title?: string
  Icon: LucideIcon
  tone: KindTone
}

export interface KindGroup {
  label: string
  kinds: KindInfo[]
}

const people = SHARED_EXPENSES_ENABLED

export const KIND_GROUPS: KindGroup[] = [
  {
    label: 'Money out',
    kinds: [
      {
        kind: 'spent',
        label: 'Spent',
        hint: 'Groceries, bills, shopping',
        Icon: ShoppingCart,
        tone: 'out',
      },
      ...(people
        ? ([
            {
              kind: 'split',
              label: 'Split a bill',
              hint: 'Share a cost — others owe you their part',
              Icon: Split,
              tone: 'out',
            },
            {
              kind: 'paidForOthers',
              label: 'Paid for someone else',
              hint: 'Not your expense — they owe it all',
              Icon: Gift,
              tone: 'out',
            },
          ] as KindInfo[])
        : []),
    ],
  },
  {
    label: 'Money in',
    kinds: [
      {
        kind: 'received',
        label: 'Received',
        hint: 'Salary, interest, gifts',
        Icon: ArrowDownLeft,
        tone: 'in',
      },
      {
        kind: 'refund',
        label: 'Refund',
        hint: 'Money back on a purchase',
        Icon: Undo2,
        tone: 'in',
      },
    ],
  },
  {
    label: 'Bills & moving money',
    kinds: [
      {
        kind: 'cardBill',
        label: 'Pay card bill',
        hint: 'Clear a credit card from a bank account',
        Icon: CreditCard,
        tone: 'move',
      },
      {
        kind: 'transfer',
        label: 'Transfer',
        hint: 'Between your own accounts',
        Icon: ArrowLeftRight,
        tone: 'move',
      },
      {
        kind: 'emi',
        label: 'Pay an EMI',
        hint: 'Principal and interest in one go',
        Icon: Landmark,
        tone: 'move',
      },
      {
        kind: 'goal',
        label: 'Set aside for a goal',
        hint: 'Earmark money in an account',
        Icon: Target,
        tone: 'in',
      },
    ],
  },
  ...(people
    ? [
        {
          label: 'People',
          kinds: [
            {
              kind: 'lend',
              label: 'Lend or borrow',
              hint: 'Cash given or taken',
              Icon: User,
              tone: 'out',
            },
          ] as KindInfo[],
        },
      ]
    : []),
]

export function kindInfo(kind: AddKind): KindInfo {
  for (const group of KIND_GROUPS) {
    const found = group.kinds.find((k) => k.kind === kind)
    if (found) return found
  }
  throw new Error(`Unknown add kind: ${kind}`)
}

export const TONE_TILE: Record<KindTone, string> = {
  out: 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]',
  in: 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]',
  move: 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]',
}
